import React, { useState, useEffect } from "react";
import { format, isValid } from "date-fns";
import { Calendar as CalendarIcon, Clock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Custom DateTimePicker component
 * Props:
 * - value: ISO string, YYYY-MM-DDTHH:mm string, or Date object
 * - onChange: callback receiving formatted YYYY-MM-DDTHH:mm string or ""
 * - placeholder: custom placeholder
 * - disabled: boolean
 * - className: custom styling
 */
export function DateTimePicker({
  value,
  onChange,
  placeholder = "Pick date & time",
  disabled = false,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [hour, setHour] = useState("09");
  const [minute, setMinute] = useState("00");
  const [period, setPeriod] = useState("AM");

  // Parse incoming value
  useEffect(() => {
    if (!value) {
      setSelectedDate(null);
      return;
    }
    const d = new Date(value);
    if (isValid(d)) {
      setSelectedDate(d);
      const rawHour = d.getHours();
      const p = rawHour >= 12 ? "PM" : "AM";
      const h12 = rawHour % 12 === 0 ? 12 : rawHour % 12;
      setHour(String(h12).padStart(2, "0"));
      setMinute(String(d.getMinutes()).padStart(2, "0"));
      setPeriod(p);
    }
  }, [value]);

  const emitDateTime = (dateObj, hStr, mStr, pStr) => {
    if (!dateObj) {
      onChange("");
      return;
    }
    let h = parseInt(hStr, 10);
    if (pStr === "PM" && h < 12) h += 12;
    if (pStr === "AM" && h === 12) h = 0;
    const m = parseInt(mStr, 10);

    const updated = new Date(dateObj);
    updated.setHours(h, m, 0, 0);

    // Format local YYYY-MM-DDTHH:mm
    const year = updated.getFullYear();
    const month = String(updated.getMonth() + 1).padStart(2, "0");
    const day = String(updated.getDate()).padStart(2, "0");
    const hrs = String(updated.getHours()).padStart(2, "0");
    const mins = String(updated.getMinutes()).padStart(2, "0");
    onChange(`${year}-${month}-${day}T${hrs}:${mins}`);
  };

  const handleDateSelect = (newDate) => {
    if (!newDate) {
      setSelectedDate(null);
      onChange("");
      return;
    }
    setSelectedDate(newDate);
    emitDateTime(newDate, hour, minute, period);
  };

  const handleHourChange = (newHour) => {
    setHour(newHour);
    if (selectedDate) emitDateTime(selectedDate, newHour, minute, period);
  };

  const handleMinuteChange = (newMinute) => {
    setMinute(newMinute);
    if (selectedDate) emitDateTime(selectedDate, hour, newMinute, period);
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    if (selectedDate) emitDateTime(selectedDate, hour, minute, newPeriod);
  };

  const setPreset = (offsetDays, presetHour, presetPeriod) => {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    setSelectedDate(d);
    setHour(presetHour);
    setMinute("00");
    setPeriod(presetPeriod);
    emitDateTime(d, presetHour, "00", presetPeriod);
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    setSelectedDate(null);
    onChange("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal h-10 px-3 border-input bg-background hover:bg-accent/40 text-sm",
            !selectedDate && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate">
            {selectedDate
              ? `${format(selectedDate, "MMM dd, yyyy")} at ${hour}:${minute} ${period}`
              : placeholder}
          </span>
          {selectedDate && !disabled && (
            <X
              className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground shrink-0 ml-1.5"
              onClick={clearSelection}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-50 shadow-xl border bg-card" align="start">
        <div className="flex flex-col sm:flex-row">
          {/* Calendar Picker */}
          <div className="p-2 border-b sm:border-b-0 sm:border-r border-border">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              initialFocus
            />
            {/* Quick shortcuts */}
            <div className="grid grid-cols-3 gap-1 px-2 pt-1 pb-2 border-t border-border/60">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => setPreset(0, "10", "AM")}
              >
                Today 10 AM
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => setPreset(1, "11", "AM")}
              >
                Tomorrow
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => setPreset(7, "02", "PM")}
              >
                Next Week
              </Button>
            </div>
          </div>

          {/* Time Selector */}
          <div className="p-4 flex flex-col justify-between w-64 bg-muted/20">
            <div>
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                <Clock className="w-3.5 h-3.5" />
                Select Time
              </div>

              {/* Digital Time Controls */}
              <div className="flex items-center justify-center gap-2 mb-4 bg-background p-3 rounded-lg border border-border shadow-sm">
                {/* Hour */}
                <select
                  value={hour}
                  onChange={(e) => handleHourChange(e.target.value)}
                  className="bg-muted px-2 py-1.5 rounded font-mono text-base font-semibold focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
                >
                  {Array.from({ length: 12 }, (_, i) => {
                    const hVal = String(i + 1).padStart(2, "0");
                    return (
                      <option key={hVal} value={hVal}>
                        {hVal}
                      </option>
                    );
                  })}
                </select>

                <span className="font-bold text-muted-foreground">:</span>

                {/* Minute */}
                <select
                  value={minute}
                  onChange={(e) => handleMinuteChange(e.target.value)}
                  className="bg-muted px-2 py-1.5 rounded font-mono text-base font-semibold focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
                >
                  {["00", "15", "30", "45"].map((mVal) => (
                    <option key={mVal} value={mVal}>
                      {mVal}
                    </option>
                  ))}
                </select>

                {/* Period */}
                <div className="flex rounded-md border border-border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => handlePeriodChange("AM")}
                    className={cn(
                      "px-2 py-1 text-xs font-bold transition-colors",
                      period === "AM"
                        ? "bg-primary text-primary-foreground"
                        : "bg-background text-muted-foreground hover:bg-muted"
                    )}
                  >
                    AM
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePeriodChange("PM")}
                    className={cn(
                      "px-2 py-1 text-xs font-bold transition-colors",
                      period === "PM"
                        ? "bg-primary text-primary-foreground"
                        : "bg-background text-muted-foreground hover:bg-muted"
                    )}
                  >
                    PM
                  </button>
                </div>
              </div>

              {/* Common Time Presets */}
              <div className="space-y-1">
                <p className="text-[11px] font-medium text-muted-foreground mb-1">Standard Slots</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {[
                    { label: "10:00 AM", h: "10", m: "00", p: "AM" },
                    { label: "12:00 PM", h: "12", m: "00", p: "PM" },
                    { label: "03:00 PM", h: "03", m: "00", p: "PM" },
                    { label: "06:00 PM", h: "06", m: "00", p: "PM" },
                  ].map((slot) => (
                    <button
                      key={slot.label}
                      type="button"
                      onClick={() => {
                        setHour(slot.h);
                        setMinute(slot.m);
                        setPeriod(slot.p);
                        if (selectedDate) emitDateTime(selectedDate, slot.h, slot.m, slot.p);
                      }}
                      className="px-2 py-1 text-xs text-left rounded border border-border bg-background hover:bg-primary/10 hover:border-primary/40 transition-colors"
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <Button
              type="button"
              size="sm"
              className="w-full mt-4"
              onClick={() => {
                if (!selectedDate) {
                  const now = new Date();
                  handleDateSelect(now);
                }
                setOpen(false);
              }}
            >
              Done
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
export default DateTimePicker;
